import type { DayView, RuleGroupView, RulesView, VersionView } from '@tabel/shared/schemas/rules';
import type { PlanResponse } from '@tabel/shared/schemas/rules-plan';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  CalendarDays,
  CircleCheck,
  Download,
  History,
  Info,
  MoreHorizontal,
  Plus,
  Printer,
  Search,
  TriangleAlert,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useToast } from '../../components/ErrorToast';
import { useUnsavedGuard } from '../../components/UnsavedGuard';
import {
  Button,
  Callout,
  Dropdown,
  IconButton,
  MenuItem,
  PageHeader,
  Segmented,
  Skeleton,
  Tabs,
  fieldCls,
} from '../../components/ui';
import { api, downloadHistoryXlsx, rulesKeys } from '../../lib/api';
import { useSetCrumbs } from '../../lib/crumbs';
import { reportClientError } from '../../lib/errors';
import { RULES_PRINT_PATH } from '../../lib/nav';
import { useRole } from '../../lib/role';
import type { AuthedPageProps } from '../../page-route';
import { CatalogDialog } from './CatalogDialog';
import { ChangesView } from './ChangesView';
import { DayPanel } from './DayPanel';
import { DraftBar } from './DraftBar';
import type { OpenDay } from './ExampleDay';
import { GoldensView } from './GoldensView';
import { HistoryView } from './HistoryView';
import { HOW_ID, HowDayIsCounted, sectionAnchor } from './HowDayIsCounted';
import { LoadError, shouldReport } from './LoadError';
import { PinGoldenDialog } from './PinGoldenDialog';
import { PlanPanel, mayPublishPlan, usePlan } from './PlanPanel';
import { PlansView, draftPlanOf, usePlans } from './PlansView';
import { PublishDialog } from './PublishDialog';
import { RequestsView } from './RequestsView';
import { type CardEdit, type EditBase, RuleCard } from './RuleCard';
import { Terms } from './Terms';
import { type Values, fieldIndex, plainAnswer } from './answer';
import { PinContext, type PinDay } from './pin';
import { PLAN_LABEL } from './plan-texts';
import { searchRules, termRefs } from './search';
import { LABEL, T, say } from './texts';

/*
 * Страница «Правила расчёта» (004, US1; T045; contracts/ui.md, «Шапка», «Виды», «Вид «Правила»»): правила объекта на
 * дату простыми словами — «Как система считает день», 8 разделов с карточками правил, «Термины»; виды «Что
 * изменится» (при черновике), «История изменений», «Проверочные примеры» (администратор правил и утверждающий, US5),
 * «Просьбы о правилах» (они же и администратор системы, US6). Адрес: `date` — правила на дату; `start`, `end` —
 * период табеля (выбор версии); `from` — откуда пришли («Вернуться к табелю»); `#<правило>` — прокрутка к правилу.
 * Один интерфейс на 375, 768 и 1280 px: на телефоне поиск и «Правила на дату» свёрнуты в кнопки шапки. Тексты правил
 * приходят с сервера. Окно «Сделать проверочным примером» — одно на странице: его открывают пример правила, «Выбрать
 * день» и списки дней влияния (`pin.tsx`). «Добавить правило» (администратор правил, US6) открывает каталог;
 * «Добавить» открывает правило каталога в изменении в его разделе.
 * `plan` — план изменения ИИ-агента по ссылке (005, US5, T122; FR-037, FR-038): правая панель плана (`PlanPanel`);
 * «Опубликовать по плану» открывает то же окно публикации с основанием из плана и `planId`. «Закрыть» убирает план из
 * адреса. Пока план открыт, кнопка публикации полосы черновика — тоже «Опубликовать по плану» или её нет; неверный номер
 * в ссылке (`?plan=abc`) — панель «Такого плана нет» (итоговое ревью T160).
 */

const ISO = /^\d{4}-\d{2}-\d{2}$/u;
const isoOf = (v: string | null): string | undefined => (v && ISO.test(v) ? v : undefined);
/** «Откуда» — только путь этого сервиса: `/timesheets/7`, не `//другой.сайт` (открытый переход). */
const pathOf = (v: string | null): string | undefined =>
  v && /^\/(?![/\\])/u.test(v) && !/[\s\\]/u.test(v) ? v : undefined;
/** Номер плана из ссылки агента (`?plan=12`): целое больше нуля. */
const planOf = (v: string | null): number | undefined =>
  v && /^[1-9]\d{0,14}$/u.test(v) ? Number(v) : undefined;

type View = 'rules' | 'changes' | 'history' | 'goldens' | 'requests' | 'plans';
type Tool = 'date' | 'search' | null;

/**
 * Правила, которые объект применяет, и правила закона (FR-001). Правило каталога — только если его включили в
 * черновике или оно открыто в изменении после «Добавить» (FR-055).
 */
const shownOf = (view: RulesView, editing: string | null) =>
  view.groups
    .filter((g) => !g.inCatalog || g.law || g.draft?.changed === true || g.id === editing)
    .sort((a, b) => a.order - b.order);

/**
 * Последняя опубликованная версия (data-model Г.3): среди «действует» и «начнёт действовать» — самая поздняя дата
 * начала, при равенстве — больший номер. От неё строится черновик (FR-027).
 */
function latestPublished(versions: readonly VersionView[] | undefined): VersionView | null {
  const open = (versions ?? []).filter((v) => v.label === 'active' || v.label === 'future');
  return (
    open.sort((a, b) =>
      a.validFrom === b.validFrom ? b.version - a.version : a.validFrom < b.validFrom ? 1 : -1,
    )[0] ?? null
  );
}

function VersionLine({ view }: { view: RulesView }) {
  const v = view.version;
  const active = v.label === 'active';
  return (
    <p
      className={`inline-flex max-w-full items-start gap-1.5 rounded-control px-2.5 py-1 text-[12.5px] font-medium ${
        active ? 'bg-ok-soft text-ok' : 'bg-warn-soft text-warn'
      }`}
    >
      {active ? (
        <CircleCheck size={14} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
      ) : (
        <TriangleAlert size={14} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
      )}
      {active ? say.versionActive(v.validFrom, v.version) : say.versionOther(view.date, v.version)}
    </p>
  );
}

/**
 * Правила к запуску площадки (007, FR-004a) — подсказка о состоянии экрана (дизайн-система Callout): `accent` — копия
 * правил образца, начнут действовать при запуске, опубликовать здесь нельзя; образец изменился после начала черновика —
 * `warn`.
 */
function LaunchNotes({ launch }: { launch: NonNullable<RulesView['launch']> }) {
  return (
    <div className="space-y-2">
      <Callout tone="accent" icon={Info}>
        <b className="font-semibold">{LABEL.launchTitle}</b>{' '}
        {say.versionLaunch(launch.sourceName, launch.sourceVersion)}
      </Callout>
      {launch.sourceChanged && (
        <Callout tone="warn" icon={TriangleAlert}>
          {say.launchSourceChanged(launch.sourceName)}
        </Callout>
      )}
    </div>
  );
}

/** «Новые версии с вашего прошлого входа: N» у утверждающего (FR-062): ссылки ведут к версиям в истории. */
function NewVersions({
  versions,
  onOpen,
}: {
  versions: NonNullable<RulesView['newVersionsSinceVisit']>;
  onOpen: (id: number) => void;
}) {
  return (
    <section aria-label={LABEL.newVersions}>
      <Callout tone="accent" icon={History}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <p className="font-medium text-ink">{say.newVersions(versions.length)}</p>
          {versions.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => onOpen(v.id)}
              className="text-accent hover:underline max-sm:min-h-10"
            >
              {say.versionLink(v.version, v.validFrom)}
            </button>
          ))}
        </div>
      </Callout>
    </section>
  );
}

function PageSkeleton() {
  return (
    <div aria-busy="true" aria-label={LABEL.loading} className="space-y-6">
      <Skeleton className="h-40 w-full rounded-card" />
      {['a', 'b', 'c'].map((k) => (
        <div key={k} className="space-y-2.5">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-40 w-full rounded-card" />
        </div>
      ))}
    </div>
  );
}

export function RulesPage({ me }: AuthedPageProps) {
  useSetCrumbs([{ label: LABEL.title }]);
  const { role } = useRole();
  const { showError } = useToast();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const date = isoOf(params.get('date'));
  const end = isoOf(params.get('end'));
  const start = end ? (isoOf(params.get('start')) ?? date) : undefined;
  const period = start && end && start <= end ? { from: start, to: end } : null;
  const back = pathOf(params.get('from'));
  /** План в адресе как есть: его сохраняет смена даты. */
  const planParam = params.get('plan');
  /** Номер плана: `undefined` — плана в адресе нет; `null` — номер неверный (`?plan=abc`): «Такого плана нет». */
  const planId = planParam === null ? undefined : (planOf(planParam) ?? null);
  /** План, по которому открыто окно публикации; `null` — публикация черновика 004. */
  const [publishPlan, setPublishPlan] = useState<PlanResponse | null>(null);
  // `view=history` — сразу «История изменений» (ссылка «Ещё N пересчётов» из плашки табеля).
  const [tab, setTab] = useState<View>(() => (params.get('view') === 'history' ? 'history' : 'rules'));
  /** Версия, к которой ведёт ссылка «Новые версии с вашего прошлого входа» (FR-062). */
  const [focusVersion, setFocusVersion] = useState<number | undefined>(undefined);
  const [publishing, setPublishing] = useState(false);
  const [tool, setTool] = useState<Tool>(null);
  const [query, setQuery] = useState('');
  const [day, setDay] = useState<OpenDay | null>(null);

  const q = { date, from: period?.from, to: period?.to };
  const rules = useQuery({
    queryKey: rulesKeys.view(role, q),
    queryFn: async () => {
      try {
        return await api.rules(q);
      } catch (e) {
        if (shouldReport(e)) reportClientError('Rules', 'load rules', e);
        throw e;
      }
    },
    placeholderData: keepPreviousData,
  });
  const view = rules.data;
  const draft = view?.draft ?? null;
  const { guard } = useUnsavedGuard();
  const [editing, setEditing] = useState<string | null>(null);

  // Изменение (US2, FR-019): администратор правил; черновик строится от последней опубликованной версии. Без
  // черновика она может быть другой, чем правила на странице (будущая версия, «Правила на дату»): тогда её правила.
  // Открытое изменение остаётся, даже если черновик за это время начал вести другой (FR-027: введённое не теряется).
  // На дате прошлой версии (заменена, не введена) «Изменить» и «Добавить правило» нет (FR-011).
  const isAdmin = role === 'rules_admin';
  const label = view?.version.label;
  const canEdit = isAdmin && (label === 'active' || label === 'future') && (draft === null || draft.isLeader);
  /** План по ссылке — тот же запрос, что у панели плана: полосе черновика нужно, можно ли опубликовать по плану. */
  const planQuery = usePlan(planId ?? null);
  const versions = useQuery({
    queryKey: rulesKeys.history(role),
    queryFn: () => api.rulesHistory(),
    enabled: isAdmin && view !== undefined && draft === null,
  });
  const latest = latestPublished(versions.data);
  const latestDate = latest && view && latest.id !== view.version.id ? latest.validFrom : undefined;
  const latestView = useQuery({
    queryKey: rulesKeys.view(role, { date: latestDate }),
    queryFn: () => api.rules({ date: latestDate }),
    enabled: isAdmin && draft === null && latestDate !== undefined,
  });
  const editBase: EditBase | null = useMemo(() => {
    if (!view || !isAdmin) return null;
    if (draft) {
      const future = draft.baseLabel === 'future' && draft.baseValidFrom;
      return { view, note: future ? say.editFromFuture(draft.baseValidFrom as string) : null };
    }
    if (versions.isLoading) return 'loading';
    const note = latest?.label === 'future' ? say.editFromFuture(latest.validFrom) : null;
    if (!latestDate) return { view, note };
    if (latestView.data) return { view: latestView.data, note };
    return latestView.isError ? { view, note: null } : 'loading';
  }, [view, isAdmin, draft, versions.isLoading, latest, latestDate, latestView.data, latestView.isError]);

  // «Проверочные примеры» — администратор правил и утверждающий (таблица «Права ролей»).
  const seesGoldens = role === 'rules_admin' || role === 'approver';
  // «Сделать проверочным примером» — только администратор правил: одно окно на странице (FR-051).
  const [pinning, setPinning] = useState<{ day: OpenDay; view?: DayView } | null>(null);
  const pin = useMemo<PinDay | null>(
    () => (isAdmin ? (d: OpenDay, v?: DayView) => setPinning({ day: d, view: v }) : null),
    [isAdmin],
  );

  // «Просьбы о правилах» — администратор правил, утверждающий и администратор системы (FR-057).
  const seesRequests = seesGoldens || role === 'sysadmin';
  // «Планы агентов» и строка о плане в полосе черновика (005, FR-065) — права плана по ссылке: администратор правил и
  // утверждающий.
  const seesPlans = seesGoldens;
  const plans = usePlans(seesPlans);
  const draftPlan = draftPlanOf(plans.data?.plans);

  // Черновик пропал (опубликован, отменён) — вида «Что изменится» нет; роль сменилась — видов, которых ей нет, тоже.
  useEffect(() => {
    if (
      (!draft && tab === 'changes') ||
      (!seesGoldens && tab === 'goldens') ||
      (!seesRequests && tab === 'requests') ||
      (!seesPlans && tab === 'plans')
    )
      setTab('rules');
  }, [draft, tab, seesGoldens, seesRequests, seesPlans]);

  // «Добавить правило» (US6, FR-054, FR-055): каталог — правила версии, от которой строится изменение. «Добавить»
  // открывает правило в изменении в его разделе со значениями `seed`; закрыли изменение — правило снова в каталоге.
  const [catalog, setCatalog] = useState(false);
  const [added, setAdded] = useState<Readonly<Record<string, Values>>>({});
  const [scrollTo, setScrollTo] = useState<string | null>(null);
  const catalogGroups = editBase && editBase !== 'loading' ? editBase.view.groups : (view?.groups ?? []);
  const addRule = (id: string, seed: Values) => {
    setCatalog(false);
    guard(() => {
      setAdded({ [id]: seed });
      setEditing(id);
      setTab('rules');
      setScrollTo(id);
    });
  };

  const shown = useMemo(() => (view ? shownOf(view, editing) : []), [view, editing]);

  // Правило из каталога открыто в изменении — прокрутка к нему, когда оно отрисовано.
  // biome-ignore lint/correctness/useExhaustiveDependencies: карточка появляется со сменой `shown`
  useEffect(() => {
    if (!scrollTo) return;
    const el = document.getElementById(scrollTo);
    if (!el) return;
    setScrollTo(null);
    el.scrollIntoView?.({ block: 'start' });
  }, [scrollTo, shown]);
  const fields = useMemo(() => fieldIndex(view?.groups ?? []), [view]);
  const byId = useMemo(() => new Map<string, RuleGroupView>(shown.map((g) => [g.id, g])), [shown]);
  const terms = useMemo(() => termRefs(view?.terms ?? []), [view]);
  const plain = useMemo(() => new Map(shown.map((g) => [g.id, plainAnswer(g, fields)])), [shown, fields]);
  const hits = useMemo(() => searchRules(shown, plain, terms, query), [shown, plain, terms, query]);

  // Переход по адресу (ссылка на правило, другая дата) закрывает правую панель дня.
  // biome-ignore lint/correctness/useExhaustiveDependencies: реагируем только на смену адреса
  useEffect(() => setDay(null), [location.key]);

  // `#<правило>` в адресе — прокрутка к правилу, когда оно отрисовано; один раз на адрес и дату правил: фоновое
  // обновление правил страницу не дёргает.
  const hash = location.hash.slice(1);
  const scrolled = useRef('');
  useEffect(() => {
    if (!view || !hash || tab !== 'rules') return;
    const key = `${location.key}|${hash}|${view.date}`;
    if (scrolled.current === key) return;
    const el = document.getElementById(decodeURIComponent(hash));
    if (!el) return;
    scrolled.current = key;
    el.scrollIntoView?.({ block: 'start' });
  }, [view, hash, tab, location.key]);

  /** Новые параметры адреса: «Правила на дату» вручную или «Сегодня» — без периода табеля; «откуда» и план остаются. */
  const setDate = (next: string | undefined) => {
    const p = new URLSearchParams();
    if (next) p.set('date', next);
    if (back) p.set('from', back);
    if (planParam !== null) p.set('plan', planParam);
    setParams(p);
  };
  /** «Открыть план» из списка планов и полосы черновика (FR-065): план в адрес — как по ссылке агента. */
  const openPlanById = (id: number) => {
    const p = new URLSearchParams(params);
    p.set('plan', String(id));
    setParams(p);
  };
  /** «Закрыть» у панели плана: план уходит из адреса, остальное остаётся. */
  const closePlan = () => {
    const p = new URLSearchParams(params);
    p.delete('plan');
    setParams(p, { replace: true });
  };
  const openPublish = (plan: PlanResponse | null) =>
    guard(() => {
      setPublishPlan(plan);
      setPublishing(true);
    });
  const closePublish = () => {
    setPublishing(false);
    setPublishPlan(null);
  };
  // Пока открыт план (итоговое ревью T160): «Опубликовать» полосы черновика ведёт к публикации по плану — тот же
  // путь, что «Опубликовать по плану» панели, с `planId`. Нельзя опубликовать по плану (устарел, ещё не загрузился,
  // неверная ссылка, не ведущий) — кнопки в полосе нет: черновик мимо открытого плана не публикуется, причину говорит
  // панель. Закрыли план — полоса публикует черновик, как в 004.
  const openPlan = planQuery.data;
  const publishByPlan = mayPublishPlan(openPlan, rules.error ? null : draft, role) ? openPlan : null;
  // Правила к запуску (007, FR-004a) публикует запуск площадки, не страница правил.
  const draftPublish =
    !(isAdmin && draft?.isLeader) || view?.launch
      ? undefined
      : planId === undefined
        ? () => openPublish(null)
        : publishByPlan
          ? () => openPublish(publishByPlan)
          : undefined;
  /** Версия периода табеля: правила на начало версии в периоде; период и «откуда» остаются. */
  const pickVersion = (from: string) => {
    const p = new URLSearchParams(params);
    p.set('date', from);
    if (start) p.set('start', start);
    setParams(p);
  };
  const toggle = (t: Exclude<Tool, null>) => setTool((cur) => (cur === t ? null : t));
  const printDate = view?.date ?? date;

  const header = (
    <PageHeader
      title={
        <>
          {LABEL.title} <span className="font-normal text-ink-2">· {me.object.name}</span>
        </>
      }
      description={T.intro}
      meta={view && !rules.error && !view.launch && <VersionLine view={view} />}
      actions={
        <>
          <IconButton
            icon={CalendarDays}
            label={LABEL.date}
            aria-expanded={tool === 'date'}
            onClick={() => toggle('date')}
            className="border border-line sm:hidden"
          />
          <IconButton
            icon={Search}
            label={LABEL.search}
            aria-expanded={tool === 'search'}
            onClick={() => toggle('search')}
            className="border border-line sm:hidden"
          />
          <div
            data-tool="date"
            className={`flex min-w-0 items-center gap-2 ${tool === 'date' ? 'max-sm:order-last max-sm:w-full' : 'max-sm:hidden'}`}
          >
            <input
              type="date"
              aria-label={LABEL.date}
              value={view?.date ?? date ?? ''}
              onChange={(e) => {
                if (ISO.test(e.target.value)) setDate(e.target.value);
              }}
              className={`${fieldCls} w-[150px] min-w-0 tabular-nums max-sm:flex-1`}
            />
            <Button disabled={!date} onClick={() => setDate(undefined)}>
              {LABEL.today}
            </Button>
          </div>
          <div
            data-tool="search"
            className={`relative flex min-w-0 items-center ${tool === 'search' ? 'max-sm:order-last max-sm:w-full' : 'max-sm:hidden'}`}
          >
            <Search
              size={16}
              strokeWidth={1.75}
              className="pointer-events-none absolute left-2.5 text-ink-3"
              aria-hidden
            />
            <input
              type="search"
              aria-label={LABEL.search}
              placeholder={T.searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className={`${fieldCls} w-[220px] min-w-0 pr-9 pl-8 max-sm:w-full [&::-webkit-search-cancel-button]:hidden`}
            />
            {query && (
              <IconButton
                icon={X}
                label={LABEL.searchClear}
                onClick={() => setQuery('')}
                className="absolute right-0.5"
              />
            )}
          </div>
          <Dropdown
            align="end"
            width="w-[260px]"
            trigger={({ open, toggle: t }) => (
              <IconButton
                icon={MoreHorizontal}
                label={LABEL.actions}
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={t}
                className="border border-line"
              />
            )}
          >
            {(close) => (
              <>
                <MenuItem
                  icon={Printer}
                  onClick={() => {
                    close();
                    window.open(
                      `${RULES_PRINT_PATH}${printDate ? `?date=${printDate}` : ''}`,
                      '_blank',
                      'noopener',
                    );
                  }}
                >
                  {LABEL.printRules}
                </MenuItem>
                <MenuItem
                  icon={Download}
                  onClick={() => {
                    close();
                    downloadHistoryXlsx().catch((e) =>
                      showError(e, {
                        screen: 'Rules',
                        action: 'download history',
                        retry: () => void downloadHistoryXlsx(),
                      }),
                    );
                  }}
                >
                  {LABEL.downloadHistory}
                </MenuItem>
              </>
            )}
          </Dropdown>
          {/* Администратор правил; при чужом черновике — нет (таблица «Права ролей»). Главная кнопка — последняя. */}
          {canEdit && view && !rules.error && (
            <Button variant="primary" icon={Plus} onClick={() => setCatalog(true)}>
              {LABEL.addRule}
            </Button>
          )}
        </>
      }
    />
  );

  const found = query.trim() !== '' && view && (
    <section
      aria-label={LABEL.found}
      className="rounded-card border border-line bg-surface px-4 py-3 text-[13.5px] break-words"
    >
      {hits.length > 0 ? (
        <>
          <p className="text-ink-2">{say.found(hits.length)}</p>
          <ul className="mt-1.5 space-y-0.5">
            {hits.map((g) => (
              <li key={g.id}>
                <a
                  href={`#${g.id}`}
                  onClick={() => setTab('rules')}
                  className="inline-block py-0.5 text-accent hover:underline max-sm:flex max-sm:min-h-10 max-sm:items-center"
                >
                  {g.question}
                </a>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p>
          {say.notFound(query.trim())}{' '}
          <a href={`#${HOW_ID}`} onClick={() => setTab('rules')} className="text-accent hover:underline">
            {LABEL.howTitle}
          </a>
        </p>
      )}
    </section>
  );

  const periodVersions =
    view?.periodVersions && view.periodVersions.length > 1 && start ? view.periodVersions : null;
  const tabs: { value: View; label: string }[] = [
    { value: 'rules', label: LABEL.tabRules },
    ...(draft ? [{ value: 'changes' as const, label: LABEL.tabChanges }] : []),
    { value: 'history', label: LABEL.tabHistory },
    ...(seesGoldens ? [{ value: 'goldens' as const, label: LABEL.goldens }] : []),
    ...(seesRequests ? [{ value: 'requests' as const, label: LABEL.tabRequests }] : []),
    ...(seesPlans ? [{ value: 'plans' as const, label: PLAN_LABEL.tab }] : []),
  ];
  const showWas = view?.version.label === 'active';
  /** Изменение карточки: «Изменить» и редактор — администратору правил; ответ по черновику — всем, кто его видит. */
  const cardEdit = (id: string, today: string): CardEdit | undefined =>
    isAdmin || draft
      ? {
          canEdit,
          open: isAdmin && editing === id,
          onOpen: () =>
            guard(() => {
              setAdded({});
              setEditing(id);
            }),
          onClose: () => {
            setEditing((cur) => (cur === id ? null : cur));
            setAdded({});
          },
          base: editBase ?? 'loading',
          draft,
          showWas,
          today,
          seed: editing === id ? added[id] : undefined,
        }
      : undefined;

  return (
    <PinContext.Provider value={pin}>
      <div className="mx-auto w-full max-w-[880px] space-y-6">
        {back && (
          <Link
            to={back}
            className="-mb-2 inline-flex items-center gap-1.5 text-[13px] text-ink-2 hover:text-ink"
          >
            <ArrowLeft size={16} strokeWidth={1.75} aria-hidden />
            {LABEL.back}
          </Link>
        )}
        {header}
        {role === 'approver' && view && !rules.error && (view.newVersionsSinceVisit ?? []).length > 0 && (
          <NewVersions
            versions={view.newVersionsSinceVisit ?? []}
            onOpen={(id) => {
              setFocusVersion(id);
              setTab('history');
            }}
          />
        )}
        {view?.launch && !rules.error && <LaunchNotes launch={view.launch} />}
        {draft && !rules.error && (
          <DraftBar
            draft={draft}
            onChanges={() => setTab('changes')}
            onPublish={draftPublish}
            publishLabel={planId === undefined ? undefined : PLAN_LABEL.publish}
            plan={
              draftPlan && draftPlan.planId !== planId
                ? { item: draftPlan, tzOffset: me.object.tzOffset, onOpen: openPlanById }
                : undefined
            }
          />
        )}
        {view && draft && isAdmin && draft.isLeader && (
          <PublishDialog
            open={publishing}
            view={view}
            draft={draft}
            plan={publishPlan ?? undefined}
            tzOffset={me.object.tzOffset}
            onClose={closePublish}
            onStale={closePublish}
            onPublished={(res) => {
              // Сценарий US3 7: после публикации — «История изменений» с итогом пересчёта. По плану (US5, сценарий 2)
              // — ещё версия выделена («Подготовлено ИИ-агентом»), а план уходит из адреса.
              const byPlan = publishPlan !== null;
              closePublish();
              setEditing(null);
              setFocusVersion(byPlan ? res.version.id : undefined);
              setTab('history');
              if (byPlan) closePlan();
            }}
          />
        )}
        {found}
        <Tabs label={LABEL.views} value={tab} onChange={setTab} idPrefix="rules-view" items={tabs} />
        {tab === 'changes' && view && draft ? (
          <div role="tabpanel" id="rules-view-panel-changes" aria-labelledby="rules-view-tab-changes">
            <ChangesView view={view} onOpenDay={setDay} onRule={() => setTab('rules')} />
          </div>
        ) : tab === 'goldens' && seesGoldens ? (
          <div role="tabpanel" id="rules-view-panel-goldens" aria-labelledby="rules-view-tab-goldens">
            <GoldensView tzOffset={me.object.tzOffset} />
          </div>
        ) : tab === 'requests' && seesRequests ? (
          <div role="tabpanel" id="rules-view-panel-requests" aria-labelledby="rules-view-tab-requests">
            <RequestsView tzOffset={me.object.tzOffset} />
          </div>
        ) : tab === 'plans' && seesPlans ? (
          <div role="tabpanel" id="rules-view-panel-plans" aria-labelledby="rules-view-tab-plans">
            <PlansView tzOffset={me.object.tzOffset} onOpen={openPlanById} />
          </div>
        ) : tab !== 'history' ? (
          <div
            role="tabpanel"
            id="rules-view-panel-rules"
            aria-labelledby="rules-view-tab-rules"
            className="space-y-8"
          >
            {rules.isLoading && <PageSkeleton />}
            {rules.error && (
              <LoadError
                error={rules.error}
                title={LABEL.loadError}
                onRetry={() => void rules.refetch()}
                objectId={me.object.id}
                onToday={() => setDate(undefined)}
              />
            )}
            {view && !rules.error && (
              <>
                {periodVersions && start && (
                  <Callout tone="accent" icon={Info}>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                      <span>{T.periodTwo}</span>
                      {/* Версий может быть больше двух: на телефоне сегменты переносятся, страница вбок не едет. */}
                      <Segmented
                        className="flex-wrap"
                        label={LABEL.periodVersions}
                        value={String(view.version.id)}
                        onChange={(id) => {
                          const v = periodVersions.find((x) => String(x.id) === id);
                          if (v) pickVersion(v.validFrom > start ? v.validFrom : start);
                        }}
                        options={periodVersions.map((v) => ({
                          value: String(v.id),
                          label: say.versionInPeriod(v.version, v.validFrom > start ? v.validFrom : start),
                        }))}
                      />
                    </div>
                  </Callout>
                )}
                <HowDayIsCounted sections={view.sections} />
                {[...view.sections]
                  .sort((a, b) => a.n - b.n)
                  .map((s) => {
                    const cards = shown.filter((g) => g.section === s.id);
                    return (
                      <section
                        key={s.id}
                        id={sectionAnchor(s.id)}
                        aria-labelledby={`${sectionAnchor(s.id)}-title`}
                        className="scroll-mt-4 space-y-2.5"
                      >
                        <h2
                          id={`${sectionAnchor(s.id)}-title`}
                          className="text-[12px] font-semibold tracking-[0.07em] text-ink-3 uppercase"
                        >
                          {`${s.n}. ${s.title}`}
                        </h2>
                        <p className="-mt-1 text-[13px] text-ink-2">{s.sentence}</p>
                        {cards.length === 0 && <p className="text-[13px] text-ink-3">{T.sectionEmpty}</p>}
                        {cards.map((g) => (
                          <RuleCard
                            key={g.id}
                            group={g}
                            byId={byId}
                            fields={fields}
                            terms={terms}
                            date={view.date}
                            tzOffset={me.object.tzOffset}
                            onOpenDay={setDay}
                            edit={cardEdit(g.id, view.today)}
                          />
                        ))}
                      </section>
                    );
                  })}
                <Terms terms={terms} />
              </>
            )}
          </div>
        ) : (
          <div role="tabpanel" id="rules-view-panel-history" aria-labelledby="rules-view-tab-history">
            <HistoryView
              tzOffset={me.object.tzOffset}
              focus={focusVersion}
              draftOpen={view && !rules.error ? draft !== null : undefined}
            />
          </div>
        )}
        <DayPanel day={day} onClose={() => setDay(null)} tzOffset={me.object.tzOffset} />
        {planId !== undefined && (
          <PlanPanel
            planId={planId}
            open={day === null}
            draft={rules.error ? null : draft}
            groups={view?.groups ?? []}
            tzOffset={me.object.tzOffset}
            onClose={closePlan}
            onPublish={(plan) => openPublish(plan)}
            onOpenVersion={(id) => {
              closePlan();
              setFocusVersion(id);
              setTab('history');
            }}
          />
        )}
        {isAdmin && (
          <PinGoldenDialog
            day={pinning?.day ?? null}
            view={pinning?.view}
            tzOffset={me.object.tzOffset}
            onClose={() => setPinning(null)}
          />
        )}
        {canEdit && (
          <CatalogDialog
            open={catalog}
            groups={catalogGroups}
            onClose={() => setCatalog(false)}
            onAdd={addRule}
          />
        )}
      </div>
    </PinContext.Provider>
  );
}
